import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Trophy } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * iter143 — Automated Gameweek Engine banner.
 *
 * Fetches the per-slug gameweek/match/day counts from the PUBLIC endpoint
 * `/api/public/gameweek/{slug}` (no auth required — needed on the login
 * page before sign-in). Renders `Gameweek [N]: [X] matches over [Y] days.`
 * Returns null silently when the slug is unknown OR any of the three
 * fields is unset (no back-fill — user directive).
 *
 * Two visual variants:
 *   • `variant="pill"` (default) — compact, sits next to another banner
 *     on the Predictions / League pages (per user layout ask).
 *   • `variant="stacked"` — full-width, sits above the prize banner on
 *     the Login page.
 *
 * Slug resolution priority:
 *   1. `slugOverride` prop (used for login screen where useParams is set).
 *   2. `useParams().slug` (in-app tenant routes).
 * If neither resolves the banner returns null.
 */
export default function GameweekBanner({ slugOverride = null, variant = "pill" }) {
  const params = useParams();
  const slug = slugOverride || params.slug || null;
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!slug) { setData(null); return; }
    (async () => {
      try {
        // iter170 — add a cache-busting query param so intermediate
        // proxies / browser bfcache / PWA offline layers can't serve a
        // stale response after a Close-Season click. Backend also
        // returns `Cache-Control: no-store`; belt-and-braces.
        const bust = Date.now();
        const { data } = await axios.get(`${API_BASE}/api/public/gameweek/${slug}?_=${bust}`);
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled) setData(null);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (!data) return null;
  const { current_gameweek_number: n, match_count: m, day_count: d } = data;
  if (n == null || m == null || d == null) return null;

  const label = `Gameweek ${n}: ${m} matches over ${d} days.`;

  if (variant === "stacked") {
    return (
      <section
        data-testid="gameweek-banner"
        className="rounded-sm border border-amber-300 bg-amber-50 text-amber-900 px-4 py-3 flex items-center gap-3"
      >
        <Trophy className="h-4 w-4 shrink-0 text-amber-700" />
        <p className="text-sm font-semibold leading-tight">{label}</p>
      </section>
    );
  }
  // pill — yellow. Explicit `min-h-[96px]` matches the prize banner's
  // natural height so both cards appear as a single, balanced row (user
  // spec: "same size … same depth"). Text is centred vertically and
  // never wraps (single-line whitespace-nowrap keeps the pill neat on
  // wider viewports; overflow-x-auto guards narrow phones).
  return (
    <div
      data-testid="gameweek-banner"
      className="self-stretch rounded-sm border border-amber-300 bg-amber-50 text-amber-900 px-5 min-h-[96px] flex items-center gap-3 whitespace-nowrap overflow-x-auto"
    >
      <Trophy className="h-5 w-5 shrink-0 text-amber-700" />
      <p className="text-sm sm:text-base font-bold leading-snug">{label}</p>
    </div>
  );
}
